/* ============================================================
   My Shopstop V1160 — Desktop catalog menu + responsive smart header controller
   ------------------------------------------------------------
   Shopify/Xtra's existing #nav-bar remains the only menu source.
   The hamburger itself has no JavaScript geometry: it is a real
   header-flow element. This controller owns menu open state, active
   first-level item state, overlay surface dimensions and the responsive
   sticky header behavior.

   V1161 — RAIL engine for phones and tablets (both orientations) and any
   viewport <= 1000px: the header bar is position:sticky inside an absolutely
   positioned rail that spans #root. Its movement is native scrolling — the
   browser's compositor moves it exactly 1:1 with the page, from the first
   pixel, with momentum, independent of main-thread load. JavaScript only
   re-anchors the bar when the scroll direction changes (after a 6px
   tolerance): on the way down it releases the bar at its current document
   position (sticky top:-height, so the page carries it away); on the way up it
   parks the bar right above the viewport edge (rail ends at that point, so the
   page brings it back in and the browser re-pins it at the top). No
   transform, no transition and no per-frame writes.

   V1160 — direction-aware endpoint engine (desktop with a fine pointer). JavaScript only decides the direction-aware endpoint state
   (visible / hidden) and toggles .ss-smart-header-hidden; the movement is the
   220 ms CSS transform transition, which the browser runs on the compositor.
   The former V603 touch renderer (inline transform written from every
   scroll/touchmove event) is removed: on touch browsers scroll runs on the
   compositor and scroll events reach the main thread one or more frames late,
   so a JS-written transform always lagged behind the finger ("sticks, then
   jumps"). Height-only viewport resizes (mobile/tablet browser toolbar
   collapsing during the gesture) are geometry noise and never reset or reveal
   the header; only a genuine width/orientation change does.
   ============================================================ */
(function () {
  'use strict';

  var desktopQuery = window.matchMedia('(min-width: 1001px)');

  /* V1162 — tablet diagnosis. Both features are OFF unless the URL carries
     the parameter, so the default behaviour is byte-for-byte V1161 logic.
     ?ss-header=rail|class|pinned forces the smart-header engine for this tab
     (remembered in sessionStorage; ?ss-header=auto restores the default).
     ?ss-debug=header shows a small on-screen measurement panel
     (?ss-debug=off removes it). */
  function readSessionFlag(name, pattern, offValue) {
    var value = '';
    try {
      var match = new RegExp('[?&]' + name + '=(' + pattern + ')(?:&|$)').exec(window.location.search);
      if (match) {
        value = match[1];
        if (value === offValue) window.sessionStorage.removeItem('ss-' + name);
        else window.sessionStorage.setItem('ss-' + name, value);
      } else {
        value = window.sessionStorage.getItem('ss-' + name) || '';
      }
    } catch (error) {
      value = '';
    }
    return value === offValue ? '' : value;
  }

  var forcedEngine = readSessionFlag('ss-header', 'rail|class|pinned|auto', 'auto');
  var debugHeader = readSessionFlag('ss-debug', 'header|off', 'off') === 'header';
  var html = document.documentElement;

  function directChildren(element, tagName) {
    return Array.prototype.filter.call(element.children, function (child) {
      return child.tagName === tagName;
    });
  }

  function getTopLevelItems(list) {
    return directChildren(list, 'LI').filter(function (item) {
      return !item.classList.contains('show-all');
    });
  }

  function getDirectPanel(item) {
    return directChildren(item, 'UL')[0] || null;
  }

  function setElementInert(element, value) {
    if (element && 'inert' in element) element.inert = value;
  }

  function initDesktopMenu(section) {
    if (!section || section.dataset.ssDesktopMenuReady === 'true') return;

    var trigger = section.querySelector('[data-ss-desktop-menu-trigger]');
    var nav = section.querySelector('#header-outer > #nav-bar[data-ss-desktop-menu]');
    var list = nav && nav.querySelector(':scope > ul[data-type="horizontal-nav"]');
    var headerOuter = section.querySelector('#header-outer');
    var headerInner = section.querySelector('#header-inner');
    var stickyShell = section.closest('#site-header');

    if (!trigger || !nav || !list || !headerOuter || !headerInner) return;

    section.dataset.ssDesktopMenuReady = 'true';

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var signal = controller ? controller.signal : undefined;
    var listenerOptions = signal ? { signal: signal } : undefined;
    var passiveOptions = signal ? { passive: true, signal: signal } : { passive: true };
    var items = getTopLevelItems(list);
    var panels = items.map(getDirectPanel).filter(Boolean);
    var open = false;
    var activeItem = null;
    var geometryFrame = 0;
    var scrollFrame = 0;
    var sizeObserver = null;
    var headerHidden = false;
    var stickyLayoutReady = section.classList.contains('ss-sticky-layout-ready');
    var lastScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
    var scrollDirection = 0;
    var directionTravel = 0;
    var scrollDirectionThreshold = 10;
    var topRevealThreshold = 12;
    var viewportWidth = Math.round(window.innerWidth || document.documentElement.clientWidth || 0);
    var interactionBlocked = false;
    var interactionLockClass = 'ss-smart-header-interaction-locked';
    var htmlStateObserver = null;
    var touchPrimaryQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
    var rail = stickyShell ? stickyShell.querySelector(':scope > .ss-header-rail') : null;
    var bar = rail ? rail.querySelector(':scope > .ss-header-bar') : null;
    var railModeClass = 'ss-header-rail-mode';
    var railActive = false;
    var railMode = 'reveal';
    var railLimit = Infinity;
    var railBarHeight = 0;
    var railBase = 0;
    var railRootTop = 0;
    var railTravelThreshold = 6;
    var railObserver = null;

    section.ssDesktopMenuController = controller;

    function setPanelState(active) {
      items.forEach(function (item) {
        var panel = getDirectPanel(item);
        var isActive = item === active;

        item.classList.toggle('ss-desktop-menu-active', isActive);

        if (panel) {
          panel.setAttribute('aria-hidden', isActive && open ? 'false' : 'true');
          setElementInert(panel, !(isActive && open));
        }
      });

      activeItem = active || null;
    }

    function getInitialItem() {
      return items.find(function (item) {
        return !!getDirectPanel(item);
      }) || items[0] || null;
    }

    function getRailHeight() {
      return Math.ceil(list.scrollHeight || list.getBoundingClientRect().height || 0);
    }

    function getPanelHeight(item) {
      var panel = item && getDirectPanel(item);
      if (!panel) return 0;
      return Math.ceil(panel.scrollHeight || panel.getBoundingClientRect().height || 0);
    }

    function getDesiredWidth() {
      return panels.length ? 1088 : 230;
    }

    function sizeSurface() {
      var railHeight = getRailHeight();
      var panelHeight = getPanelHeight(activeItem || getInitialItem());
      var viewportLimit = Math.max(260, window.innerHeight - headerOuter.getBoundingClientRect().bottom - 12);
      var naturalHeight = Math.max(railHeight, panelHeight);
      var height = Math.max(220, Math.min(naturalHeight, viewportLimit));

      nav.style.setProperty('--ss-desktop-menu-height', height + 'px');
      section.classList.toggle('ss-desktop-menu-scroll', naturalHeight > viewportLimit);
    }

    function positionSurface() {
      if (!desktopQuery.matches || !open) return;

      var outerRect = headerOuter.getBoundingClientRect();
      var innerRect = headerInner.getBoundingClientRect();
      var triggerRect = trigger.getBoundingClientRect();
      var leftBoundary = Math.max(outerRect.left, innerRect.left);
      var rightBoundary = Math.min(outerRect.right, innerRect.right);
      var desiredLeftViewport = Math.max(leftBoundary, triggerRect.left);
      var availableFromTrigger = Math.max(230, rightBoundary - desiredLeftViewport);
      var desiredWidth = Math.min(getDesiredWidth(), availableFromTrigger);
      var left = Math.max(0, desiredLeftViewport - outerRect.left);

      nav.style.setProperty('--ss-desktop-menu-left', Math.round(left) + 'px');
      nav.style.setProperty('--ss-desktop-menu-width', Math.round(desiredWidth) + 'px');

      sizeSurface();
    }

    function requestGeometry() {
      if (!desktopQuery.matches || !open) return;
      window.cancelAnimationFrame(geometryFrame);
      geometryFrame = window.requestAnimationFrame(positionSurface);
    }

    function setActive(item) {
      setPanelState(item);
      requestGeometry();
    }

    function openMenu() {
      if (!desktopQuery.matches || open) return;

      open = true;
      trigger.setAttribute('aria-expanded', 'true');
      nav.setAttribute('aria-hidden', 'false');
      setElementInert(nav, false);
      setPanelState(getInitialItem());
      section.classList.add('ss-desktop-menu-open');
      html.classList.add('ss-desktop-nav-open');
      positionSurface();
    }

    function closeMenu(options) {
      options = options || {};
      open = false;
      section.classList.remove('ss-desktop-menu-open', 'ss-desktop-menu-scroll');
      html.classList.remove('ss-desktop-nav-open');
      trigger.setAttribute('aria-expanded', 'false');
      setPanelState(null);
      nav.style.removeProperty('--ss-desktop-menu-height');

      if (desktopQuery.matches) {
        nav.setAttribute('aria-hidden', 'true');
        setElementInert(nav, true);
      } else {
        nav.removeAttribute('aria-hidden');
        setElementInert(nav, false);
      }

      if (options.restoreFocus && desktopQuery.matches) trigger.focus();
    }

    function currentScrollY() {
      return Math.max(0, window.scrollY || window.pageYOffset || 0);
    }

    function setHeaderHidden(hidden) {
      hidden = !!hidden;

      /* The rail engine never uses the hidden class; "visible" means pinned. */
      if (railActive) {
        if (!hidden) setRailMode('reveal', Infinity);
        return;
      }

      /* V1162 diagnosis: ?ss-header=pinned keeps the header always visible. */
      if (hidden && forcedEngine === 'pinned') return;

      if (headerHidden === hidden) return;

      headerHidden = hidden;
      section.classList.toggle('ss-smart-header-hidden', hidden);
      if (stickyShell) stickyShell.classList.toggle('ss-smart-header-hidden', hidden);

      if (hidden && open) closeMenu();
    }

    function resetScrollTracking(revealHeader) {
      lastScrollY = currentScrollY();
      scrollDirection = 0;
      directionTravel = 0;
      if (revealHeader) setHeaderHidden(false);
    }

    /* Search, drawer and header forms temporarily own the header. The shell's
       transform is released completely (interaction-locked) so fixed / overlay
       descendants keep the viewport as their containing block. */
    function suspendHeader() {
      if (!stickyShell) return;

      if (scrollFrame) {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = 0;
      }

      interactionBlocked = true;
      headerHidden = false;
      section.classList.remove('ss-smart-header-hidden');
      stickyShell.classList.remove('ss-smart-header-hidden');
      stickyShell.classList.add(interactionLockClass);
      if (railActive) setRailMode('reveal', Infinity);
      resetScrollTracking(false);

      if (open) closeMenu();
    }

    function resumeHeader() {
      if (!stickyShell) return;

      interactionBlocked = false;
      headerHidden = false;
      section.classList.remove('ss-smart-header-hidden');
      stickyShell.classList.remove('ss-smart-header-hidden', interactionLockClass);
      resetScrollTracking(false);
    }

    function hasExplicitBlockingHeaderInteraction() {
      return html.classList.contains('m2a') ||
        html.classList.contains('search-focus') ||
        html.classList.contains('search-full') ||
        html.classList.contains('search-full-mode') ||
        html.classList.contains('search-compact-active') ||
        html.classList.contains('user-form-active');
    }

    function isHeaderFormControl(element) {
      return !!(element && section.contains(element) && /^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName));
    }

    function hasBlockingHeaderInteraction() {
      if (hasExplicitBlockingHeaderInteraction()) return true;
      return isHeaderFormControl(document.activeElement);
    }

    function syncHeaderInteractionState() {
      if (!stickyShell) return;

      var blocked = hasBlockingHeaderInteraction();
      if (blocked === interactionBlocked) return;

      if (blocked) suspendHeader();
      else resumeHeader();
    }

    /* ---------------- V1161 rail engine ---------------- */
    function railWanted() {
      if (forcedEngine === 'class' || forcedEngine === 'pinned') return false;
      return !!(rail && bar && stickyShell &&
        stickyShell.classList.contains('ss-sticky-flow-shell') &&
        stickyLayoutReady && section.classList.contains('ss-sticky-layout-ready') &&
        (forcedEngine === 'rail' || !desktopQuery.matches || touchPrimaryQuery.matches));
    }

    /* Bar top in rail coordinates (rail top = #root padding edge). */
    function railBarPosition(y) {
      if (railMode === 'release') return Math.max(railLimit, y - railBarHeight);
      var pinned = Math.max(railBase, y);
      return railLimit === Infinity ? pinned : Math.min(pinned, railLimit);
    }

    function writeRail() {
      if (!rail || !bar) return;

      if (railMode === 'release') {
        rail.style.removeProperty('bottom');
        rail.style.removeProperty('height');
        bar.style.marginTop = railLimit + 'px';
        bar.style.top = (-railBarHeight) + 'px';
        return;
      }

      bar.style.marginTop = railBase + 'px';
      bar.style.top = '0px';
      if (railLimit === Infinity) {
        rail.style.removeProperty('bottom');
        rail.style.removeProperty('height');
      } else {
        rail.style.bottom = 'auto';
        rail.style.height = (railLimit + railBarHeight) + 'px';
      }
    }

    function setRailMode(mode, limit) {
      if (!railActive) return;
      if (railMode === mode && railLimit === limit) return;
      railMode = mode;
      railLimit = limit;
      writeRail();
      if (mode === 'release' && open) closeMenu();
    }

    function measureRail() {
      railBarHeight = bar.getBoundingClientRect().height;
      stickyShell.style.setProperty('--ss-header-rail-h', railBarHeight + 'px');
      railBase = stickyShell.offsetTop;
      var rootElement = stickyShell.offsetParent || document.documentElement;
      railRootTop = rootElement.getBoundingClientRect().top + currentScrollY();
    }

    function enableRail() {
      railActive = true;
      headerHidden = false;
      section.classList.remove('ss-smart-header-hidden');
      stickyShell.classList.remove('ss-smart-header-hidden');
      stickyShell.style.removeProperty('transform');
      stickyShell.style.setProperty('--ss-header-rail-h', bar.getBoundingClientRect().height + 'px');
      stickyShell.classList.add(railModeClass);
      measureRail();
      railMode = 'reveal';
      railLimit = Infinity;
      writeRail();
      resetScrollTracking(false);

      if (!railObserver && typeof ResizeObserver !== 'undefined') {
        railObserver = new ResizeObserver(function () {
          if (!railActive) return;
          var height = bar.getBoundingClientRect().height;
          if (Math.abs(height - railBarHeight) < 0.5) return;
          railBarHeight = height;
          stickyShell.style.setProperty('--ss-header-rail-h', height + 'px');
          writeRail();
        });
        railObserver.observe(bar);
      }
    }

    function disableRail() {
      railActive = false;
      railMode = 'reveal';
      railLimit = Infinity;
      if (railObserver) {
        railObserver.disconnect();
        railObserver = null;
      }
      if (stickyShell) {
        stickyShell.classList.remove(railModeClass);
        stickyShell.style.removeProperty('--ss-header-rail-h');
      }
      if (rail) {
        rail.style.removeProperty('bottom');
        rail.style.removeProperty('height');
      }
      if (bar) {
        bar.style.removeProperty('margin-top');
        bar.style.removeProperty('top');
      }
      resetScrollTracking(false);
    }

    function syncRailMode() {
      if (railWanted()) {
        if (!railActive) {
          enableRail();
        } else {
          measureRail();
          railMode = 'reveal';
          railLimit = Infinity;
          writeRail();
        }
      } else if (railActive) {
        disableRail();
      }
    }

    function updateRail() {
      var scrollY = currentScrollY();
      var delta = scrollY - lastScrollY;
      lastScrollY = scrollY;

      if (hasBlockingHeaderInteraction()) {
        if (!interactionBlocked) suspendHeader();
        else setRailMode('reveal', Infinity);
        return;
      }

      if (interactionBlocked) resumeHeader();
      if (Math.abs(delta) < 0.5) return;

      var y = scrollY - railRootTop;
      var direction = delta > 0 ? 1 : -1;
      if (direction !== scrollDirection) {
        scrollDirection = direction;
        directionTravel = 0;
      }
      directionTravel += Math.abs(delta);

      if (direction > 0) {
        /* Down: after the tolerance, release the bar where it is now. */
        if (railMode === 'release' || directionTravel < railTravelThreshold) return;
        setRailMode('release', railBarPosition(y));
        return;
      }

      /* Up. Once a revealed bar is pinned again, drop the rail limit so the
         next downward tolerance applies from a clean pinned state. */
      if (railMode === 'reveal') {
        if (railLimit !== Infinity && y <= railLimit) setRailMode('reveal', Infinity);
        return;
      }

      /* Released bar: park it at its current position with the rail ending
         there; the page now carries it back in. If the viewport already
         reached the bar, pin immediately so it never detaches from the top. */
      if (y <= railLimit || directionTravel >= railTravelThreshold) {
        setRailMode('reveal', railBarPosition(y));
      }
    }

    function syncViewportResize() {
      var nextViewportWidth = Math.round(window.innerWidth || document.documentElement.clientWidth || 0);
      var widthChanged = Math.abs(nextViewportWidth - viewportWidth) > 2;
      viewportWidth = nextViewportWidth;

      requestGeometry();

      /* V1160: mobile/tablet browser toolbars collapse and expand DURING the
         scroll gesture and fire height-only resize events. On every viewport
         (also tablet landscape >= 1001px, which uses this same engine) such an
         event must neither reveal the header nor reset the direction
         accumulator. A genuine width / orientation change still resets. */
      if (!widthChanged) {
        syncHeaderInteractionState();
        return;
      }

      syncRailMode();
      resetScrollTracking(true);
      syncHeaderInteractionState();
    }

    function updateSmartHeader() {
      scrollFrame = 0;

      if (railActive) {
        updateRail();
        return;
      }

      var scrollY = currentScrollY();
      var delta = scrollY - lastScrollY;

      /* Never act until Xtra has initialized the stable sticky-flow geometry.
         Once ss:sticky-header-ready fires, tracking restarts from the current
         position with no synthetic movement. */
      if (!stickyLayoutReady || !section.classList.contains('ss-sticky-layout-ready')) {
        resetScrollTracking(true);
        return;
      }

      if (hasBlockingHeaderInteraction()) {
        if (!interactionBlocked) suspendHeader();
        lastScrollY = scrollY;
        return;
      }

      if (interactionBlocked) resumeHeader();

      lastScrollY = scrollY;

      if (Math.abs(delta) >= 1) {
        var nextDirection = delta > 0 ? 1 : -1;
        if (nextDirection !== scrollDirection) {
          scrollDirection = nextDirection;
          directionTravel = 0;
        }
        directionTravel += Math.abs(delta);
      }

      if (scrollY <= topRevealThreshold) {
        setHeaderHidden(false);
        return;
      }

      if (Math.abs(delta) < 1) return;
      if (directionTravel < scrollDirectionThreshold) return;

      if (scrollDirection < 0) {
        directionTravel = 0;
        setHeaderHidden(false);
        return;
      }

      if (!section.classList.contains('fixed')) return;

      directionTravel = 0;
      setHeaderHidden(true);
    }

    function requestSmartHeader() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updateSmartHeader);
    }

    function handleWindowScroll() {
      requestGeometry();

      /* Rail engine: consume the scroll offset immediately (no rAF); it only
         writes when the direction changes. */
      if (railActive) {
        updateRail();
        return;
      }

      requestSmartHeader();
    }

    function handleTouchMove() {
      if (railActive) updateRail();
    }

    function syncViewportMode() {
      if (scrollFrame) {
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = 0;
      }
      viewportWidth = Math.round(window.innerWidth || document.documentElement.clientWidth || 0);
      syncRailMode();
      resetScrollTracking(true);
      syncHeaderInteractionState();

      if (desktopQuery.matches) {
        closeMenu();
      } else {
        open = false;
        section.classList.remove('ss-desktop-menu-open', 'ss-desktop-menu-scroll');
        html.classList.remove('ss-desktop-nav-open');
        trigger.setAttribute('aria-expanded', 'false');
        setPanelState(null);
        nav.removeAttribute('aria-hidden');
        setElementInert(nav, false);
        nav.style.removeProperty('--ss-desktop-menu-left');
        nav.style.removeProperty('--ss-desktop-menu-width');
        nav.style.removeProperty('--ss-desktop-menu-height');
      }
    }

    trigger.addEventListener('click', function () {
      if (!desktopQuery.matches) return;
      if (open) closeMenu();
      else openMenu();
    }, listenerOptions);

    var captureListenerOptions = signal ? { capture: true, signal: signal } : true;

    section.addEventListener('focusin', function (event) {
      if (isHeaderFormControl(event.target)) {
        suspendHeader();
      } else {
        setHeaderHidden(false);
      }
    }, captureListenerOptions);

    section.addEventListener('focusout', function (event) {
      if (!interactionBlocked) return;
      if (hasExplicitBlockingHeaderInteraction()) return;
      if (isHeaderFormControl(event.relatedTarget)) return;
      resumeHeader();
    }, captureListenerOptions);

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        if (!open || !desktopQuery.matches) return;
        setActive(item);
      }, listenerOptions);

      item.addEventListener('focusin', function () {
        if (!open || !desktopQuery.matches) return;
        setActive(item);
      }, listenerOptions);
    });

    document.addEventListener('pointerdown', function (event) {
      if (!open || !desktopQuery.matches) return;
      if (nav.contains(event.target) || trigger.contains(event.target)) return;
      closeMenu();
    }, listenerOptions);

    document.addEventListener('keydown', function (event) {
      if (!open || !desktopQuery.matches || event.key !== 'Escape') return;
      event.preventDefault();
      closeMenu({ restoreFocus: true });
    }, listenerOptions);

    window.addEventListener('ss:sticky-header-ready', function (event) {
      var readySection = event.detail && event.detail.section;
      if (readySection && readySection !== section) return;
      stickyLayoutReady = true;
      syncRailMode();
      resetScrollTracking(true);
      syncHeaderInteractionState();
    }, listenerOptions);

    window.addEventListener('resize', syncViewportResize, passiveOptions);
    window.addEventListener('scroll', handleWindowScroll, passiveOptions);
    window.addEventListener('touchmove', handleTouchMove, passiveOptions);

    if (touchPrimaryQuery.addEventListener) {
      touchPrimaryQuery.addEventListener('change', syncViewportMode, listenerOptions);
    } else if (touchPrimaryQuery.addListener) {
      touchPrimaryQuery.addListener(syncViewportMode);
    }

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', syncViewportMode, listenerOptions);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(syncViewportMode);
    }

    if (typeof ResizeObserver !== 'undefined') {
      sizeObserver = new ResizeObserver(function () {
        requestGeometry();
      });
      sizeObserver.observe(headerInner);
      sizeObserver.observe(list);
      panels.forEach(function (panel) {
        sizeObserver.observe(panel);
      });
    }

    if (typeof MutationObserver !== 'undefined') {
      htmlStateObserver = new MutationObserver(function () {
        syncHeaderInteractionState();
      });
      htmlStateObserver.observe(html, { attributes: true, attributeFilter: ['class'] });
    }

    section.ssDesktopMenuCleanup = function () {
      if (controller) controller.abort();
      if (sizeObserver) sizeObserver.disconnect();
      if (htmlStateObserver) htmlStateObserver.disconnect();
      window.cancelAnimationFrame(geometryFrame);
      window.cancelAnimationFrame(scrollFrame);
      disableRail();
      section.classList.remove('ss-smart-header-hidden');
      if (stickyShell) {
        stickyShell.classList.remove('ss-smart-header-hidden', interactionLockClass);
        stickyShell.style.removeProperty('transform');
      }
      html.classList.remove('ss-desktop-nav-open');
    };

    syncViewportMode();
  }

  /* V1162 — on-screen measurement panel (only with ?ss-debug=header). It
     measures what a phone/tablet really does while scrolling: frame gaps of the
     main thread, scroll-event spacing and long tasks, plus which header engine
     is active. Nothing is sent anywhere; it only draws a small box. */
  function initHeaderDebugPanel() {
    if (!debugHeader || document.getElementById('ss-header-debug')) return;

    var panel = document.createElement('div');
    panel.id = 'ss-header-debug';
    panel.setAttribute('aria-hidden', 'true');
    panel.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483647;max-width:calc(100vw - 16px);' +
      'padding:8px 10px;border-radius:8px;background:rgba(0,0,0,.82);color:#fff;font:12px/1.35 monospace;' +
      'white-space:pre;pointer-events:auto';
    document.body.appendChild(panel);

    var stats = { frames: 0, over34: 0, over50: 0, over100: 0, worstFrame: 0, scrolls: 0, worstScrollGap: 0, longTasks: 0, worstLongTask: 0, switches: 0 };
    var lastFrame = 0;
    var lastScroll = 0;
    var scrolling = false;
    var scrollIdleTimer = 0;
    var shell = document.getElementById('site-header');
    var bar = shell && shell.querySelector('.ss-header-bar');
    var lastBarStyle = '';

    function reset() {
      Object.keys(stats).forEach(function (key) { stats[key] = 0; });
    }

    panel.addEventListener('click', reset);

    window.addEventListener('scroll', function () {
      var now = performance.now();
      if (scrolling && lastScroll) stats.worstScrollGap = Math.max(stats.worstScrollGap, now - lastScroll);
      lastScroll = now;
      stats.scrolls += 1;
      scrolling = true;
      window.clearTimeout(scrollIdleTimer);
      scrollIdleTimer = window.setTimeout(function () { scrolling = false; lastFrame = 0; }, 180);
    }, { passive: true });

    function frame(now) {
      if (scrolling) {
        if (lastFrame) {
          var gap = now - lastFrame;
          stats.frames += 1;
          stats.worstFrame = Math.max(stats.worstFrame, gap);
          if (gap > 34) stats.over34 += 1;
          if (gap > 50) stats.over50 += 1;
          if (gap > 100) stats.over100 += 1;
        }
        lastFrame = now;
      }
      if (bar) {
        var style = bar.style.marginTop + '|' + bar.style.top;
        if (style !== lastBarStyle) {
          if (lastBarStyle) stats.switches += 1;
          lastBarStyle = style;
        }
      }
      window.requestAnimationFrame(frame);
    }
    window.requestAnimationFrame(frame);

    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (!scrolling) return;
          stats.longTasks += 1;
          stats.worstLongTask = Math.max(stats.worstLongTask, entry.duration);
        });
      }).observe({ entryTypes: ['longtask'] });
    } catch (error) {
      stats.longTasks = -1;
    }

    function engineName() {
      if (!shell) return 'sin #site-header';
      if (shell.classList.contains('ss-header-rail-mode')) return 'rail';
      return forcedEngine === 'pinned' ? 'pinned' : 'class';
    }

    function render() {
      var ua = navigator.userAgent;
      var browser = /CriOS|Chrome\/(\d+)/.test(ua) ? 'Chrome ' + (ua.match(/(?:CriOS|Chrome)\/(\d+)/) || [])[1] :
        /SamsungBrowser\/(\d+)/.test(ua) ? 'Samsung ' + ua.match(/SamsungBrowser\/(\d+)/)[1] :
        /Version\/(\d+[.\d]*).*Safari/.test(ua) ? 'Safari ' + ua.match(/Version\/(\d+[.\d]*)/)[1] :
        /Firefox\/(\d+)/.test(ua) ? 'Firefox ' + ua.match(/Firefox\/(\d+)/)[1] : 'otro';
      var os = /iPad|Macintosh/.test(ua) && navigator.maxTouchPoints > 1 ? 'iPadOS' : /iPhone/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : 'desktop';
      panel.textContent =
        'SS-HEADER V1162  motor: ' + engineName() + (forcedEngine ? ' (forzado)' : '') + '\n' +
        os + ' \u00b7 ' + browser + ' \u00b7 ' + window.innerWidth + '\u00d7' + window.innerHeight + ' @' + (window.devicePixelRatio || 1) + 'x' +
        ' \u00b7 ' + (window.matchMedia('(pointer: coarse)').matches ? 't\u00e1ctil' : 'rat\u00f3n') + '\n' +
        'scroll: ' + stats.frames + ' fotogramas \u00b7 >34ms ' + stats.over34 + ' \u00b7 >50ms ' + stats.over50 + ' \u00b7 >100ms ' + stats.over100 + '\n' +
        'peor fotograma ' + Math.round(stats.worstFrame) + 'ms \u00b7 peor hueco scroll ' + Math.round(stats.worstScrollGap) + 'ms\n' +
        'tareas largas ' + (stats.longTasks < 0 ? 'n/d' : stats.longTasks + ' (peor ' + Math.round(stats.worstLongTask) + 'ms)') +
        ' \u00b7 cambios carril ' + stats.switches + '\n' +
        '(tocar el panel = poner a cero)';
    }
    render();
    window.setInterval(render, 500);
  }

  if (debugHeader) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHeaderDebugPanel, { once: true });
    else initHeaderDebugPanel();
  }

  function initAll(root) {
    if (!root) return;

    if (root.matches && root.matches('.shopify-section-header')) {
      initDesktopMenu(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll('.shopify-section-header').forEach(initDesktopMenu);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target && event.target.matches && event.target.matches('.shopify-section-header')
      ? event.target
      : event.target && event.target.querySelector && event.target.querySelector('.shopify-section-header');

    if (section && section.ssDesktopMenuCleanup) section.ssDesktopMenuCleanup();
  });
})();
